import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { SchemaForm } from 'platform/forms-system/exportsFile';
import FormNavButtons from 'platform/forms-system/src/js/components/FormNavButtons';
import { setEditMode } from 'platform/forms-system/src/js/actions';
import { mask, capitalizeFirstLetter } from '../../utils/helpers';
import { hasEnteredBankAccount } from './DirectDepositField';
import { PaymentInformationReviewNote } from './PaymentInformationReview';

const maskedToBullets = value => value?.replace(/\*/g, '●');

// Rows shown on R&S: the entered account, else the prefilled account
const getReviewRows = data => {
  const { directDeposit, 'view:prefilledBankAccount': prefilled } = data ?? {};

  if (hasEnteredBankAccount(directDeposit) || !prefilled) {
    return {
      accountType: capitalizeFirstLetter(directDeposit?.accountType),
      routingNumber: mask(directDeposit?.routingNumber),
      accountNumber: mask(directDeposit?.accountNumber),
      bankName: directDeposit?.bankName,
    };
  }

  return {
    accountType: capitalizeFirstLetter(prefilled['view:accountType']),
    routingNumber: maskedToBullets(prefilled['view:routingNumber']),
    accountNumber: maskedToBullets(prefilled['view:accountNumber']),
    bankName: prefilled['view:bankName'],
  };
};

const REVIEW_LABELS = [
  ['accountType', 'Account type'],
  ['routingNumber', 'Bank routing number'],
  ['accountNumber', 'Account number'],
  ['bankName', 'Bank name'],
];

export const DirectDepositPageReview = ({ data, editPage, title }) => {
  const rows = getReviewRows(data);

  return (
    <div className="form-review-panel-page">
      <div className="form-review-panel-page-header-row">
        <h4 className="form-review-panel-page-header vads-u-font-size--h5">
          {typeof title === 'function' ? title(data) : title}
        </h4>
        <va-button
          data-testid="direct-deposit-review-edit-button"
          secondary
          onClick={editPage}
          label="Edit direct deposit information"
          text="Edit"
          uswds
        />
      </div>
      <dl className="review">
        {REVIEW_LABELS.map(([key, label]) => (
          <div className="review-row" key={key}>
            <dt>{label}</dt>
            <dd className="dd-privacy-hidden">{rows[key]}</dd>
          </div>
        ))}
      </dl>
      <PaymentInformationReviewNote />
    </div>
  );
};

DirectDepositPageReview.propTypes = {
  data: PropTypes.object,
  editPage: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

// A CustomPage so R&S shows the bank card's own Save/Cancel instead of the
// chapter's "Update" button.
const DirectDepositPage = ({
  name,
  title,
  data,
  schema,
  uiSchema,
  appStateData,
  onChange,
  onSubmit,
  onReviewPage,
  setFormData,
  updatePage,
  goBack,
  onContinue,
  formContext,
  contentBeforeButtons,
  contentAfterButtons,
  NavButtons = FormNavButtons,
}) => {
  const updatePageRef = useRef(updatePage);
  updatePageRef.current = updatePage;
  const onEdit = useCallback(() => updatePageRef.current?.(), []);

  const dispatch = useDispatch();
  const onCancelEdit = useCallback(
    () => dispatch(setEditMode(name, false)),
    [dispatch, name],
  );

  const reviewFormContext = useMemo(
    () =>
      onReviewPage
        ? { ...formContext, onReviewPage, onEdit, onCancelEdit }
        : formContext,
    [onReviewPage, formContext, onEdit, onCancelEdit],
  );

  const pageUiSchema = useMemo(
    () =>
      onReviewPage
        ? {
            ...uiSchema,
            directDeposit: {
              ...uiSchema.directDeposit,
              'ui:options': {
                ...uiSchema.directDeposit['ui:options'],
                startInEdit: true,
              },
            },
          }
        : uiSchema,
    [onReviewPage, uiSchema],
  );

  return (
    <SchemaForm
      name={name}
      title={title}
      data={data}
      appStateData={appStateData}
      schema={schema}
      uiSchema={pageUiSchema}
      formContext={reviewFormContext}
      onChange={onReviewPage ? setFormData : onChange}
      onSubmit={onSubmit}
    >
      {contentBeforeButtons}
      {!onReviewPage && (
        <NavButtons goBack={goBack} goForward={onContinue} submitToContinue />
      )}
      {contentAfterButtons}
    </SchemaForm>
  );
};

DirectDepositPage.propTypes = {
  NavButtons: PropTypes.elementType,
  appStateData: PropTypes.object,
  contentAfterButtons: PropTypes.node,
  contentBeforeButtons: PropTypes.node,
  data: PropTypes.object,
  formContext: PropTypes.object,
  goBack: PropTypes.func,
  name: PropTypes.string,
  schema: PropTypes.object,
  setFormData: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  uiSchema: PropTypes.object,
  updatePage: PropTypes.func,
  onChange: PropTypes.func,
  onContinue: PropTypes.func,
  onReviewPage: PropTypes.bool,
  onSubmit: PropTypes.func,
};

export default DirectDepositPage;
